import type { EnrichmentQueueEntry } from '../../../shared/stores/useEnrichmentQueueStore';

export type EstadoCadastroDaSessao = 'registered' | 'absent' | 'unknown';

export interface EstadoDoEnvioLinkedIn {
  tom: 'success' | 'warning' | 'info';
  titulo: string;
  descricao: string;
}

export interface DiagnosticoDoResultadoLinkedIn {
  outcome?: string;
  step?: string;
  http?: number;
}

/** Lê somente campos técnicos conhecidos; nunca persiste texto livre do scraper. */
export function lerDiagnosticoDoResultadoLinkedIn(
  resultado: unknown,
): DiagnosticoDoResultadoLinkedIn {
  if (!resultado || typeof resultado !== 'object' || Array.isArray(resultado)) return {};
  const raiz = resultado as Record<string, unknown>;
  const debug =
    raiz.debug && typeof raiz.debug === 'object' && !Array.isArray(raiz.debug)
      ? (raiz.debug as Record<string, unknown>)
      : {};
  const outcome =
    typeof raiz.status === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(raiz.status)
      ? raiz.status
      : undefined;
  const stepRaw = raiz.step ?? debug.step;
  const step =
    typeof stepRaw === 'string' && /^[a-z][a-z0-9_]{0,47}$/.test(stepRaw) ? stepRaw : undefined;
  const httpRaw = raiz.http ?? debug.http;
  const http =
    typeof httpRaw === 'number' && Number.isInteger(httpRaw) && httpRaw >= 100 && httpRaw <= 599
      ? httpRaw
      : undefined;

  return {
    outcome,
    step: step ?? (outcome === 'resolve_forbidden' && http === 403 ? 'resolve' : undefined),
    http,
  };
}

/** A existência de sessão e a confirmação de envio são medições diferentes. */
export function rotuloDoCadastroDaSessao(estado: EstadoCadastroDaSessao): string {
  if (estado === 'registered') return 'Sessão cadastrada';
  if (estado === 'absent') return 'Sessão não cadastrada';
  return 'Não foi possível consultar o cadastro da sessão';
}

/** Traduz apenas o último job acompanhado neste navegador; não presume histórico do servidor. */
export function estadoDoEnvioLinkedIn(
  entrada: EnrichmentQueueEntry | undefined,
): EstadoDoEnvioLinkedIn {
  if (!entrada) {
    return {
      tom: 'info',
      titulo: 'Envio sem confirmação recente',
      descricao: 'Não há um resultado de envio acompanhado nesta sessão do navegador.',
    };
  }

  const acao = entrada.sendKind === 'mensagem' ? 'Mensagem' : 'Convite';
  if (entrada.status === 'processing') {
    return {
      tom: 'info',
      titulo: `${acao} em andamento`,
      descricao: 'Aguarde o resultado terminal do job para saber se o envio foi confirmado.',
    };
  }

  if (entrada.status === 'completed' && entrada.outcome === 'sent') {
    return {
      tom: 'success',
      titulo: `${acao} confirmado pelo LinkedIn`,
      descricao:
        'Este resultado confirma um envio concluído; a sessão cadastrada, sozinha, não confirma envios futuros.',
    };
  }

  if (entrada.status === 'completed') {
    return {
      tom: 'info',
      titulo: 'Job concluído sem novo envio confirmado',
      descricao:
        entrada.outcome === 'already_invited'
          ? 'Já havia um convite pendente para esse perfil; este job não enviou outro convite.'
          : entrada.outcome === 'already_connected'
            ? 'O perfil já era uma conexão; este job não enviou um novo convite.'
            : 'O job terminou, mas o resultado não confirma que uma mensagem ou convite foi enviado.',
    };
  }

  const diagnostico = [
    entrada.failureStep ? `etapa ${entrada.failureStep}` : undefined,
    entrada.failureHttp ? `HTTP ${entrada.failureHttp}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    tom: 'warning',
    titulo: `${acao} não confirmado${diagnostico ? ` · ${diagnostico}` : ''}`,
    descricao:
      entrada.failureHttp === 403
        ? 'O LinkedIn recusou esta etapa. Isso não prova que a sessão expirou. Confira o acesso e o perfil no LinkedIn; consulte o suporte antes de uma nova tentativa.'
        : 'O job falhou ou ficou sem confirmação. A causa da falha não está disponível neste resultado; confira o job antes de tentar novamente.',
  };
}
